import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  NOT_PERSONAL_ID_PATTERN,
  HTJ_STRING_PATTERN,
  BUSINESS_ID_PATTERN,
  EMAIL_PATTERN,
  PHONE_PATTERN,
  ValidationWarningMessage,
} from '../../constants';
import type { FormState } from '../../SpaceRenovationRequestCreateForm.type';
import type { ExecutorProps } from '../RenovationTypes.type';

import Stack from '@mui/material/Stack';
import ControlLabel from 'shared/components/ControlLabel';
import Input from 'shared/components/Input';
import ValidationWarning from 'components/validation-warning';
import { RenovationExecutorFieldContainer } from '../RenovationTypes.style';

const RenovationExecutorOrganization = ({
  idx,
  organizationNameMaxLength,
}: ExecutorProps) => {
  const {
    clearErrors,
    formState: { errors },
    register,
  } = useFormContext<FormState>();

  const baseName = useMemo(() => `renovationTypes.${idx}` as const, [idx]);

  const validateMaxLength = (maxLength: number, value: string | undefined) => {
    if (value !== undefined && value.length > maxLength)
      return `Pituus max ${maxLength} merkkiä.`;

    return true;
  };

  const validatePattern = (
    value: string | undefined,
    pattern: RegExp,
    message: string
  ) => {
    if (value !== undefined && !pattern.test(value)) return message;

    return true;
  };

  return (
    <Stack spacing={2} sx={{ width: { xs: '100%', md: '80%' } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required>Nimi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.organizationName?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.organizationName`, {
                  required: ValidationWarningMessage.Required,
                  validate: {
                    htjString: (value) =>
                      validatePattern(
                        value,
                        HTJ_STRING_PATTERN,
                        ValidationWarningMessage.HTJInvalid
                      ),
                    maxLength: (value) =>
                      validateMaxLength(organizationNameMaxLength, value),
                  },
                })}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required>Y-tunnus</ControlLabel>
          <ValidationWarning
            text={
              errors.renovationTypes?.[idx]?.organizationBusinessId?.message
            }
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.organizationBusinessId`, {
                  required: ValidationWarningMessage.Required,
                  validate: {
                    businessId: (value) =>
                      validatePattern(
                        value,
                        BUSINESS_ID_PATTERN,
                        ValidationWarningMessage.BusinessIdInvalid
                      ),
                    notPersonalId: (value) =>
                      validatePattern(
                        value,
                        NOT_PERSONAL_ID_PATTERN,
                        ValidationWarningMessage.NotPersonalId
                      ),
                  },
                })}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required>Sähköpostiosoite</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.organizationEmail?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.organizationEmail`, {
                  required: ValidationWarningMessage.Required,
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: ValidationWarningMessage.Invalid,
                  },
                })}
                placeholder="mail@mail.com"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required>Matkapuhelinnumero</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.organizationPhone?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.organizationPhone`, {
                  required: ValidationWarningMessage.RequiredInternationalPhone,
                  pattern: {
                    value: PHONE_PATTERN,
                    message: ValidationWarningMessage.InvalidInternationalPhone,
                  },
                })}
                placeholder="+35850123456"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>
    </Stack>
  );
};

export default RenovationExecutorOrganization;
